"use client";

import { useState } from "react";
import { X, Calendar, Clock, MessageSquare } from "lucide-react";
import { Button } from "../ui/Button";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  listingId: string;
  buyerId: string;
  sellerId: string;
  chatId: string;
  onReservationCreated?: (reservationId: string) => void;
}

export default function ReservationModal({
  isOpen,
  onClose,
  listingId,
  buyerId,
  sellerId,
  chatId,
  onReservationCreated,
}: ReservationModalProps) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [memo, setMemo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 오늘 날짜 (최소 선택 가능 날짜)
  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date || !time) {
      toast.error("날짜와 시간을 모두 선택해주세요.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { createReservation } = await import("../../lib/api/reservations");
      const { sendMessage } = await import("../../lib/chat/api");

      // 1. 예약 생성
      const result = await createReservation({
        listingId,
        buyerId,
        sellerId,
        chatId,
        date,
        time,
        memo,
      });

      if (!result.success || !result.reservationId) {
        toast.error(result.error || "예약 요청에 실패했습니다.");
        return;
      }

      // 2. 채팅방에 시스템 메시지 전송
      const reservationMessage = `📅 거래 예약 요청\n\n날짜: ${date}\n시간: ${time}${memo ? `\n메모: ${memo}` : ""}\n\n---RESERVATION_REQUEST---\nreservationId: ${result.reservationId}\nstatus: pending\n---RESERVATION_END---`;

      await sendMessage({
        chatId,
        senderUid: "system",
        content: reservationMessage,
      });

      toast.success("거래 예약 요청을 보냈습니다!");
      onReservationCreated?.(result.reservationId);
      onClose();

      // 폼 초기화
      setDate("");
      setTime("");
      setMemo("");
    } catch (error) {
      console.error("예약 요청 실패:", error);
      toast.error("예약 요청 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4"
        onClick={(e) => {
          // 배경 클릭 시에만 닫기 (모달 내부 클릭은 무시)
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">거래 예약하기</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* 날짜 선택 */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                날짜 선택 *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={today}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* 시간 선택 */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                <Clock className="w-4 h-4 inline mr-2" />
                시간 선택 *
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* 메모 (선택) */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                추가 메모 (선택)
              </label>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="거래 장소, 주차 정보 등을 입력해주세요."
                rows={3}
                maxLength={200}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {memo.length}/200자
              </p>
            </div>

            {/* 안내 메시지 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                📌 예약 요청을 보내면 상대방이 수락/거절할 수 있습니다.
              </p>
            </div>

            {/* 버튼 */}
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "전송 중..." : "예약 요청 보내기"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

